import React, {useRef, useEffect, useState} from "react";
import FormField from "../../forms/form-field";
import BaseModal from "../../modals/base-modal";
import useLocationStore from "../config/use-location-store";
import FormSelect from "../../forms/form-select";
import Tooltip from "../../tooltip/tooltip";
import {instanceMob} from "../../../utils/utils";
import CreateProviderModal from "./create-provider-modal";
import InlineModal from "../../info-modal/infoModal";
import {getBackendError} from "../../../utils/error-helpers";

const PaymentReceiptModal = ({show, onClose}) => {
    const formRef = useRef(null);
    const [infoModal, setInfoModal] = useState(null);
    const [userData, setUserData] = useState(null);
    const [selectedProvider, setSelectedProvider] = useState(null);
    const [providers, setProviders] = useState([]);
    const [nit, setNit] = useState("");
    const [paymentMethod, setPaymentMethod] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [showCreateProviderModal, setShowCreateProviderModal] = useState(false);
    const [receiptNumber, setReceiptNumber] = useState("");
    const [pendingValue, setPendingValue] = useState("");
    const selectedLocation = useLocationStore((state) => state.selectedLocation);

    const handlePaymentChange = (e) => {
        const val = e?.target?.value ?? e;
        const num = Number(val);
        setPaymentMethod(num);

        const form = formRef.current;
        if (form && num !== 50) {
            if (form.elements["other_payment_method_display"]) form.elements["other_payment_method_display"].value = "";
            if (form.elements["other_payment_method"]) form.elements["other_payment_method"].value = "";
        }
    };

    const handleOtherPaymentDisplayChange = (e) => {
        const form = formRef.current;
        if (!form) return;
        const hidden = form.elements["other_payment_method"];
        if (hidden) hidden.value = e.target.value;
    };

    const handleOpenCreateProviderModal = () => {
        setShowCreateProviderModal(true);
    };

    const fetchPendingDebt = (providerId) => {
        if (!providerId || !selectedLocation) return;

        instanceMob
            .get(
                `admin/accounting/provider/debt?provider_id=${providerId}&location_id=${selectedLocation.id}`
            )
            .then((res) => {
                const debt = res.data?.debt_value || 0;
                setPendingValue(debt);
            })
            .catch((err) => {
                console.error("Error cargando deuda pendiente:", err);
                setPendingValue(0);
            });
    };

    const handleProviderChange = (e) => {
        const selectedValue = e.target.value;
        setSelectedProvider(selectedValue);

        const selected = providers.find((p) => p.value === selectedValue);
        setNit(selected?.nit || "");

        fetchPendingDebt(selectedValue);
    };

    const formFields = [
        {
            Component: FormField,
            label: "Fecha",
            name: "receipt_date",
            type: "date",
            value: new Date().toISOString().split("T")[0],
            readOnly: true,
            placeholder: "Fecha del recibo",
            width: "65%",
            isLoading,
        },
        {
            Component: FormField,
            label: "# Registro",
            name: "registry_number",
            type: "text",
            value: receiptNumber,
            placeholder: "Número de registro automático",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormField,
            label: "Usuario que Registra",
            name: "registeredByUser",
            type: "text",
            value: userData
                ? `${userData.first_name} ${userData.last_name} (${userData.email})`
                : "",
            placeholder: "Usuario predeterminado",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormField,
            label: "Locación",
            name: "location_id_display",
            type: "text",
            value: selectedLocation?.name || "",
            placeholder: "Locación predeterminada",
            readOnly: true,
            isLoading,
        },
        {
            Component: FormSelect,
            label: "Nombre",
            name: "provider_id",
            options: providers,
            type: "text",
            placeholder: "Nombre de la empresa",
            searchable: true,
            isLoading,
            actionButton: handleOpenCreateProviderModal,
            required: true,
            value: selectedProvider || "",
            onChange: handleProviderChange,
            tooltip: "Selecciona el nombre del proveedor o empresa a la que se realizará el abono del pago pendiente registrado previamente."
        },
        {
            Component: FormField,
            label: "NIT",
            name: "nit",
            type: "text",
            placeholder: "automático",
            isLoading,
            required: true,
            readOnly: true,
            value: nit,
        },
        {
            Component: FormField,
            label: "Valor pendiente por pagar",
            name: "value_pending",
            type: "currency",
            placeholder: "valor automático",
            readOnly: true,
            isLoading,
            value: pendingValue,
            tooltip: "Monto total que aún se adeuda al proveedor por el servicio o compra registrada anteriormente."
        },
        {
            Component: FormField,
            label: "Monto a cancelar",
            name: "amount_to_cancel",
            type: "currency",
            placeholder: "Escribe aquí",
            isLoading,
            required: true,
            tooltip: "Especifique cuánto se pagará al proveedor en este comprobante. El sistema actualizará automáticamente el saldo pendiente."
        },
        {
            Component: FormSelect,
            label: "Método de pago",
            name: "payment_method",
            options: [
                {value: 10, label: "Tarjeta de Crédito o Débito"},
                {value: 20, label: "Transferencia"},
                {value: 30, label: "Efectivo"},
                {value: 50, label: "Otros"},
            ],
            placeholder: "Selecciona una opción",
            onChange: handlePaymentChange,
            isLoading,
            required: true,
            tooltip: "Selecciona cómo se realizará el pago: tarjeta, efectivo o transferencia bancaria."
        },
        {
            Component: FormField,
            label: "Entidad",
            name: "other_payment_method_display",
            placeholder: "Escribe aquí",
            disabled: paymentMethod !== 50,
            onChange: handleOtherPaymentDisplayChange,
            isLoading,
            required: paymentMethod === 50,
            tooltip: "Especifica por qué plataforma se hizo la transferencia: Nequi, Daviplata, Efecty u otra entidad."
        },
        {
            Component: FormField,
            label: "Concepto",
            name: "concept",
            type: "text",
            placeholder: "Escribe el concepto",
            isLoading,
            required: true,
            tooltip: "Selecciona el nombre del proveedor o empresa a la que se realizará el abono del pago pendiente registrado previamente."
        },
    ];

    const handleCloseCreateProviderModal = (newProvider) => {
        setShowCreateProviderModal(false);

        if (newProvider?.provider_id) {
            const formatted = {
                value: String(newProvider.provider_id),
                label: newProvider.name,
                nit: newProvider.nit,
            };

            setProviders((prev) => [...prev, formatted]);
            setSelectedProvider(formatted.value);
            setNit(formatted.nit);
        }
    };

    const fetchProviders = () => {
        setIsLoading(true);
        instanceMob
            .get(`admin/accounting/provider/`, {
                params: {location: selectedLocation.id},
            })
            .then((res) => res.data)
            .then((data) => {
                const formatted = data?.map((p) => ({
                    value: String(p?.provider_id),
                    label: p?.name,
                    nit: p?.nit,
                }));
                setProviders(formatted);
            })
            .catch((err) => console.error("Error cargando proveedores:", err))
            .finally(() => {
                setIsLoading(false);
            });
    };

    const fetchRegisterNumber = () => {
        if (!selectedLocation) return;
        instanceMob
            .get(`admin/accounting/register-number?location_id=${selectedLocation.id}`)
            .then((res) => {
                const number = res.data?.next_registry_number || "";
                setReceiptNumber(number);
            })
            .catch((err) => {
                console.error("Error cargando número de registro:", err);
            });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const formData = new FormData(formRef.current);
        const payload = Object.fromEntries(formData.entries());

        setIsLoading(true);

        instanceMob
            .post("admin/accounting/payment-receipt/create", payload)
            .then(() => {
                setInfoModal({
                    iconType: "success",
                    title: "Comprobante creado",
                    description: "El comprobante de pago fue registrado con éxito.",
                    primaryAction: {
                        label: "Aceptar",
                        onClick: () => {
                            setInfoModal(null);
                            onClose();
                        },
                    },
                });
            })
            .catch((err) => {
                const errorMessage = getBackendError(err);
                setInfoModal({
                    iconType: "error",
                    title: "Error",
                    description: errorMessage,
                    secondaryAction: {
                        label: "Cerrar",
                        onClick: () => setInfoModal(null),
                    },
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        const storedUser = {
            email: localStorage.getItem("email"),
            first_name: localStorage.getItem("first_name"),
            last_name: localStorage.getItem("last_name"),
            id: localStorage.getItem("id"),
        };
        setUserData(storedUser);
    }, []);

    useEffect(() => {
        fetchProviders();
        fetchRegisterNumber();
    }, [show]);

    useEffect(() => {
        const form = formRef.current;
        if (!form) return;
        if (paymentMethod !== 50) {
            if (form.elements["other_payment_method_display"]) form.elements["other_payment_method_display"].value = "";
            if (form.elements["other_payment_method"]) form.elements["other_payment_method"].value = "";
        }
    }, [paymentMethod]);

    return (
        <>
            <BaseModal
                show={show}
                onClose={onClose}
                title="Comprobante de pago"
                onSave={() => formRef.current.requestSubmit()}
            >
                <form
                    className="d-flex flex-column gap-2"
                    ref={formRef}
                    onSubmit={handleSubmit}
                >
                    {formFields.map((field, index) => {
                        const FieldComponent =
                            typeof field.Component === "string"
                                ? React.createElement(field.Component, {...field})
                                : <field.Component {...field} />;

                        const isHidden = field.type === "hidden";

                        if (!isHidden && field.tooltip) {
                            return (
                                <Tooltip
                                    key={index}
                                    title={field.label}
                                    description={field.tooltip}
                                    position="right"
                                    variant="rich"
                                >
                                    {FieldComponent}
                                </Tooltip>
                            );
                        }
                        return <React.Fragment key={index}>{FieldComponent}</React.Fragment>;
                    })}

                    <input type="hidden" name="other_payment_method"/>
                    <input type="hidden" name="location_id" value={selectedLocation?.id || ""}/>
                </form>
            </BaseModal>

            <CreateProviderModal
                show={showCreateProviderModal}
                onClose={handleCloseCreateProviderModal}
            />

            {infoModal && (
                <InlineModal
                    icon={infoModal.icon}
                    iconType={infoModal.iconType}
                    title={infoModal.title}
                    description={infoModal.description}
                    primaryAction={infoModal.primaryAction}
                    secondaryAction={infoModal.secondaryAction}
                />
            )}
        </>
    );
};

export default PaymentReceiptModal;
